import React, { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ChevronDown, ChevronUp, Search, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { PositionSequenceOptimizer, SequenceAnalysisResult } from '@/services/position-sequence-optimizer';
import { ParsedCsvRow, AnalysisParams } from './types';
import { formatCurrency } from '@/utils/formatters';
import { parseDecimal } from '@/lib/numberParsing';

interface PositionSequenceFinderProps {
  csvData: ParsedCsvRow[];
  availablePositions: string[];
  globalDefaults: AnalysisParams;
  getFileParams: (fileId: string) => AnalysisParams;
  onApplySequence: (sequence: string[]) => void;
  selectedFiles: Set<string>;
  uploads: Array<{ id: string; filename: string; }>;
}

export const PositionSequenceFinder: React.FC<PositionSequenceFinderProps> = ({
  csvData,
  availablePositions,
  globalDefaults,
  getFileParams,
  onApplySequence,
  selectedFiles,
  uploads,
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [results, setResults] = useState<SequenceAnalysisResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFileId, setSelectedFileId] = useState<string>('');

  // Get unique filenames from CSV data
  const availableFiles = useMemo(() => {
    const fileNames = new Set<string>();
    csvData.forEach(row => {
      if (row.filename) {
        fileNames.add(row.filename);
      }
    });
    
    // Match filenames to uploads to get IDs
    const files = Array.from(fileNames).map(filename => {
      const upload = uploads.find(u => u.filename === filename);
      return { filename, id: upload?.id || filename };
    });
    
    return files;
  }, [csvData, uploads]);

  // Filter CSV data for selected file
  const filteredCsvData = useMemo(() => {
    if (!selectedFileId) return [];
    
    const selectedFile = availableFiles.find(f => f.id === selectedFileId);
    if (!selectedFile) return [];
    
    return csvData.filter(row => row.filename === selectedFile.filename);
  }, [csvData, selectedFileId, availableFiles]);

  // Get position sizes for the selected file
  const filePositionSizes = useMemo(() => {
    if (filteredCsvData.length === 0) return [];
    
    const positions = new Set<string>();
    filteredCsvData.forEach(row => {
      if (row.position_size_raw) {
        positions.add(row.position_size_raw);
      }
    });
    
    return Array.from(positions).sort((a, b) => parseDecimal(a) - parseDecimal(b));
  }, [filteredCsvData]);

  const analyzeSequences = async () => {
    if (!selectedFileId) {
      toast.error('Please select a file to analyze');
      return;
    }
    
    if (filteredCsvData.length === 0 || filePositionSizes.length < 2) {
      toast.error('Selected file needs at least 2 position sizes to analyze sequences');
      return;
    }

    setIsAnalyzing(true);
    try {
      // Use file-specific parameters instead of global defaults
      const fileParams = getFileParams(selectedFileId);
      const sequenceResults = PositionSequenceOptimizer.findOptimalSequences(
        filteredCsvData,
        filePositionSizes,
        fileParams
      );
      
      setResults(sequenceResults);
      setIsOpen(true);
      
      const optimalCount = sequenceResults.filter(r => r.status === 'optimal').length;
      const goodCount = sequenceResults.filter(r => r.status === 'good').length;
      
      toast.success(
        `Analyzed ${sequenceResults.length} sequences. Found ${optimalCount} optimal, ${goodCount} good sequences.`
      );
    } catch (error) {
      console.error('Sequence analysis error:', error);
      toast.error('Failed to analyze sequences');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleApplySequence = (result: SequenceAnalysisResult) => {
    onApplySequence(result.sequence);
    toast.success(`Applied sequence: ${result.sequenceString}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'optimal':
        return <Badge className="bg-green-500 hover:bg-green-600">✅ Optimal</Badge>;
      case 'good':
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">⚠️ Good</Badge>;
      default:
        return <Badge variant="destructive">❌ Poor</Badge>;
    }
  };

  const getRatioBadge = (ratio: number) => {
    if (ratio >= 3.0) {
      return <span className="text-green-600 font-semibold">{ratio.toFixed(2)}</span>;
    } else if (ratio >= 2.0) {
      return <span className="text-yellow-600 font-semibold">{ratio.toFixed(2)}</span>;
    } else {
      return <span className="text-red-600 font-semibold">{ratio.toFixed(2)}</span>;
    }
  };

  const getWinRateBadge = (winRate: number) => {
    if (winRate <= 50) {
      return <span className="text-red-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 70) {
      return <span className="text-orange-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else if (winRate <= 75) {
      return <span className="text-yellow-600 font-semibold">{winRate.toFixed(1)}%</span>;
    } else {
      return <span className="text-green-600 font-semibold">{winRate.toFixed(1)}%</span>;
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Position Sequence Optimizer
          </div>
          <Button
            onClick={analyzeSequences}
            disabled={isAnalyzing || !selectedFileId || filteredCsvData.length === 0}
            size="sm"
          >
            <Search className="h-4 w-4 mr-2" />
            {isAnalyzing ? 'Analyzing...' : 'Find Optimal Sequences'}
          </Button>
        </CardTitle>
        
        <div className="flex items-center gap-4 mt-4">
          <div className="flex items-center gap-2 flex-1">
            <label htmlFor="file-select" className="text-sm font-medium">
              Analyze File:
            </label>
            <Select value={selectedFileId} onValueChange={setSelectedFileId}>
              <SelectTrigger className="w-[250px]" id="file-select">
                <SelectValue placeholder="Select a file to analyze" />
              </SelectTrigger>
              <SelectContent>
                {availableFiles.map((file) => (
                  <SelectItem key={file.id} value={file.id}>
                    {file.filename}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {selectedFileId && (
            <div className="text-sm text-muted-foreground">
              {filteredCsvData.length} trades, {filePositionSizes.length} position sizes
            </div>
          )}
        </div>
      </CardHeader>

      {results.length > 0 && (
        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger className="w-full">
            <CardHeader className="pt-0">
              <div className="flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors">
                <span>
                  Found {results.length} sequences ({results.filter(r => r.status === 'optimal').length} optimal)
                </span>
                {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                   <TableHeader>
                     <TableRow>
                       <TableHead className="w-[140px]">Sequence</TableHead>
                       <TableHead className="text-center w-[90px]">Start After</TableHead>
                       <TableHead className="text-center w-[90px]">Stop After</TableHead>
                       <TableHead className="text-right">Total Profit</TableHead>
                       <TableHead className="text-right">Max DD</TableHead>
                       <TableHead className="text-right">Profit/DD</TableHead>
                       <TableHead className="text-right">Win Rate</TableHead>
                       <TableHead className="text-right">Trades</TableHead>
                       <TableHead className="w-[100px]">Status</TableHead>
                       <TableHead className="w-[80px]">Action</TableHead>
                     </TableRow>
                   </TableHeader>
                  <TableBody>
                     {results.map((result, index) => (
                       <TableRow key={index}>
                         <TableCell className="font-mono text-sm">
                           {result.sequenceString}
                         </TableCell>
                         <TableCell className="text-center font-medium">
                           {result.startAfter}
                         </TableCell>
                         <TableCell className="text-center font-medium">
                           {result.stopAfter}
                         </TableCell>
                         <TableCell className="text-right font-semibold">
                           <span className={result.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                             {formatCurrency(result.totalProfit)}
                           </span>
                         </TableCell>
                         <TableCell className="text-right">
                           {formatCurrency(result.maxDrawdown)}
                         </TableCell>
                         <TableCell className="text-right">
                           {getRatioBadge(result.profitDDRatio)}
                         </TableCell>
                         <TableCell className="text-right">
                           {getWinRateBadge(result.winRate)}
                         </TableCell>
                         <TableCell className="text-right">
                           {result.totalTrades}
                         </TableCell>
                         <TableCell>
                           {getStatusBadge(result.status)}
                         </TableCell>
                         <TableCell>
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => handleApplySequence(result)}
                           >
                             Apply
                           </Button>
                         </TableCell>
                       </TableRow>
                     ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      )}
    </Card>
  );
};